import { useEffect } from "react";
import { useSeo } from "../lib/useSeo";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, CalendarX } from "lucide-react";
import { ASSETS, getUpcomingEvents } from "../lib/constants";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero } from "../components/bits";

export default function Events() {
  useSeo("/events");
  const events = getUpcomingEvents();

  // Event structured data — keeps Google/AI systems aware of dated, ticketed events.
  useEffect(() => {
    if (!events.length) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-events-ld", "1");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": events.map((e) => ({
        "@type": "Event",
        name: e.title,
        description: e.body,
        startDate: e.iso,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: "Higher Ground DC Cannabis Dispensary",
          address: {
            "@type": "PostalAddress",
            streetAddress: "1135 Okie St NE",
            addressLocality: "Washington",
            addressRegion: "DC",
            postalCode: "20002",
            addressCountry: "US",
          },
        },
        organizer: { "@id": "https://highergrounddc.com/#organization" },
        image: e.image && e.image.startsWith("/") ? `https://highergrounddc.com${e.image}` : e.image,
        url: e.cta && e.cta.href ? e.cta.href : "https://highergrounddc.com/events",
        ...(e.cta && e.cta.href
          ? { offers: { "@type": "Offer", url: e.cta.href, availability: "https://schema.org/InStock" } }
          : {}),
      })),
    });
    document.head.appendChild(ld);
    return () => ld.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div data-testid="events-page">
      <PageHero
        label="What's On at Higher Ground DC"
        title="Events at Higher Ground DC"
        subtitle="Workdays, tastings, live music, and everything in between. This is where DC's creative workspace turns into a room full of people. Here's what's on the calendar this month."
        image={ASSETS.soundBath}
        testid="events-hero"
      />

      <section className="bg-ink py-16 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          {events.length === 0 ? (
            <Reveal>
              <div className="flex flex-col items-center gap-6 border border-white/10 bg-ink-2 px-6 py-20 text-center" data-testid="events-empty">
                <CalendarX className="h-10 w-10 text-gold" />
                <p className="max-w-xl leading-relaxed text-white/60">
                  Nothing on the calendar yet. Check back soon, or get event updates sent straight to your inbox.
                </p>
                <Link to="/signup" data-testid="events-empty-btn" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
                  Get Event Updates
                </Link>
              </div>
            </Reveal>
          ) : (
          <Stagger className="space-y-6">
            {events.map((e) => {
              const id = e.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <StaggerItem key={e.title + e.day}>
                  <div
                    className="group grid overflow-hidden border border-white/10 bg-ink-2 transition-all duration-500 hover:border-gold/50 md:grid-cols-[280px_1fr]"
                    data-testid={`event-${id}`}
                  >
                    {/* Image */}
                    <div className="relative h-52 overflow-hidden md:h-auto">
                      <img src={e.image} alt={e.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent md:bg-gradient-to-r" />
                      <div className="absolute left-4 top-4 flex flex-col items-center justify-center border border-gold/40 bg-ink/80 px-4 py-2 text-center backdrop-blur">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{e.month}</span>
                        <span className="font-display text-3xl font-light leading-none text-white">{e.day}</span>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="flex flex-col justify-center gap-3 p-6 md:p-10">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">{e.host}</p>
                      <h3 className="font-display text-2xl font-light uppercase tracking-[0.06em] text-white md:text-3xl">{e.title}</h3>
                      <p className="max-w-2xl leading-relaxed text-white/60">{e.body}</p>
                      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/50"><Calendar className="h-4 w-4 text-gold" /> {e.time}</p>
                      <div className="pt-2">
                        {e.cta.href ? (
                          <a href={e.cta.href} target="_blank" rel="noopener noreferrer" data-testid={`event-cta-${id}`} className="inline-flex items-center gap-2 bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
                            {e.cta.label} <ArrowUpRight className="h-4 w-4" />
                          </a>
                        ) : (
                          <Link to={e.cta.to} data-testid={`event-cta-${id}`} className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:border-gold hover:text-gold">
                            {e.cta.label}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
          )}

          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-col items-center gap-4 border-t border-white/10 pt-10 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-white/50">21+ with valid ID · Medical patients welcome with valid card</p>
              <p className="text-xs uppercase tracking-[0.28em] text-gold">Never miss what's next</p>
              <p className="max-w-xl text-white/55">Get event updates before they hit the calendar.</p>
              <Link to="/signup" data-testid="events-signup-btn" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">Get Event Updates</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
